import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Function to check if the current path matches the nav item
  const isActivePath = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  // Function to get nav link classes based on active state
  const getNavLinkClasses = (path: string, isMobile: boolean = false) => {
    const baseClasses = isMobile
      ? "block py-2 font-medium transition duration-200"
      : "font-medium transition duration-200";

    const activeClasses = isActivePath(path)
      ? "text-wenergie-blue"
      : "text-gray-700 hover:text-wenergie-blue";

    return `${baseClasses} ${activeClasses}`;
  };

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md py-2' : 'bg-transparent py-4'}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <Link to="/" className="flex items-center">
              <img src="/assets/wenergie-wordmark.png" alt="Wenergie Logo" className="h-10 w-auto" />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className={getNavLinkClasses('/')}>
              Home
            </Link>
            <Link to="/plans" className={getNavLinkClasses('/plans')}>
              Plans
            </Link>
            <Link to="/products" className={getNavLinkClasses('/products')}>
              Products
            </Link>
            <a href="/about" className={getNavLinkClasses('/about')}>
              About Us
            </a>
            <a
              href="https://medium.com/wenergie"
              className="text-gray-700 hover:text-wenergie-blue font-medium transition duration-200"
              target="_blank"
              rel="noopener noreferrer"
            >
              Blog
            </a>
          </div>

          <div className="hidden md:block">
            <Link to="/plans" className="bg-wenergie-blue hover:bg-wenergie-blue/90 text-white px-6 py-2 rounded-full font-medium transition duration-300">
              Join the Community
            </Link>
          </div>

          {/* Mobile Navigation Toggle */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-700 hover:text-wenergie-blue focus:outline-none"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`md:hidden transition-all duration-300 overflow-hidden ${isOpen ? 'max-h-screen bg-white' : 'max-h-0'}`}>
        <div className="px-4 pt-2 pb-4 space-y-3">
          <Link to="/" className={getNavLinkClasses('/', true)} onClick={() => setIsOpen(false)}>
            Home
          </Link>
          <Link to="/plans" className={getNavLinkClasses('/plans', true)} onClick={() => setIsOpen(false)}>
            Plans
          </Link>
          <Link to="/products" className={getNavLinkClasses('/products', true)} onClick={() => setIsOpen(false)}>
            Products
          </Link>
          <a href="/about" className={getNavLinkClasses('/about', true)} onClick={() => setIsOpen(false)}>
            About Us
          </a>
          <a href="https://medium.com/wenergie" className="block py-2 text-gray-700 hover:text-wenergie-blue font-medium" onClick={() => setIsOpen(false)}>
            Blog
          </a>
          <Link
            to="/plans"
            className="block text-center bg-wenergie-blue hover:bg-wenergie-blue/90 text-white px-4 py-2 rounded-full font-medium transition duration-300 mt-4"
            onClick={() => setIsOpen(false)}
          >
            Join the Community
          </Link>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;